import { AUTHENTICATION_METHOD, USERNAME_FORMAT_CODE, USER_ACTIVATION_METHOD } from '../../domain/domainCodes.js'
/**
 * Legacy strings for إعدادات النظام, إعدادات متقدمة and بيانات الإعدادات.
 *
 * Sources: Locale/Resources.ar.resx (most of them), Admin/TermsOfUseSettings.ar.resx,
 * Admin/ProfileManagement.ar.resx, Admin/ProfileMapping.ar.resx, Admin/CompanyProfile.ar.resx,
 * Admin/NotificationSettings.ar.resx and Admin/Sitemap.ar.resx.
 *
 * Razor reaches these through Resources.Designer.cs, which rewrites every character
 * that is not valid in a C# identifier to `_` — so the comment beside each entry is
 * the Designer name the view uses, not always the literal resx key.
 */

/** Shared across all three screens. */
export const common = {
  selectFileToSave: 'اختر ملفاً قبل حفظ هذا القسم',
  save: 'حفظ', // CertificateSettings.BtnSave
  saveChanges: 'حفظ التغييرات',
  /* Save feedback. The server usually sends its own sentence; these cover the
     cases where it sends none. */
  saveFailed: 'تعذر الحفظ، فضلا حاول مرة أخرى',
  notAvailable: 'هذا الإعداد غير متاح بعد',
  loadFailed: 'تعذر تحميل الإعدادات',
  orgNamesRequired: 'اسم المنظمة مطلوب بالعربية والإنجليزية',
  termsRequired: 'نص شروط الاستخدام مطلوب بالعربية والإنجليزية قبل تفعيلها',
  reset: 'إلغاء', // General.Reset
  update: 'تحديث', // ProfileManagement.btnUpdateResource1_Text
  active: 'مفعلة', // ActiveTxt
  inactive: 'غير مفعلة', // InActiveText
  show: 'إظهار', // Show
  hide: 'إخفاء', // Hide
  yes: 'نعم', // Yes
  no: 'لا', // No
  arabic: 'اللغة العربية', // Arabic
  english: 'اللغة الإنجليزية', // English
  status: 'الحالة', // Status
  none: 'بدون', // None
  saved: 'تم الحفظ بنجاح',
}

export const settingsStrings = {
  title: 'إعدادات النظام', // System_Settings

  /**
   * الهوية البصرية — the client identity pair.
   *
   * NOT a legacy screen: tokens.css was built around `--gov-primary` and
   * `--gov-gold` as "two knobs [that] rebrand the whole app", and this is the
   * setting that turns that into something an admin can reach. The wording is
   * new because the feature is — and it is kept short, because two colour
   * fields do not need a paragraph to explain them.
   */
  theme: {
    title: 'الهوية البصرية',
    hint: 'لونان يحددان مظهر المنصة.',
    main: 'اللون الأساسي',
    active: 'لون التحديد',
    mainContrast: 'النص على الأزرار',
    activeContrast: 'التحديد على القائمة',
    pass: 'مناسب',
    weak: 'ضعيف',
    restore: 'الافتراضي',
    save: 'حفظ',
    saved: 'تمت العملية بنجاح',
  },

  /* شريط الإعدادات: أسماء مجردة، مجمعة بحسب ما يفعله المستخدم. الصفحة اسمها
     «الإعدادات» أصلا، فلا يكرر أي بند الكلمة. */
  nav: {
    groups: {
      appearance: 'المظهر',
      content: 'المحتوى',
      communication: 'التواصل',
      access: 'الدخول والحسابات',
      integration: 'التكامل',
      system: 'النظام',
    },
    identity: 'ألوان الهوية',
    media: 'الشعارات والصور',
    keys: 'الاسم والتذييل',
    contact: 'صفحة التواصل',
    video: 'تشغيل الفيديو',
    userManual: 'أدلة المستخدم',
    documentation: 'حزمة الأدلة',
    notifications: 'التنبيهات',
    email: 'خادم البريد',
    messaging: 'قنوات التنبيه',
    reports: 'التقارير',
    login: 'تسجيل الدخول',
    registration: 'التسجيل اليدوي',
    profile: 'الملف الشخصي',
    sources: 'مصادر البيانات',
    sync: 'مزامنة البيانات',
    servers: 'الخوادم',
    services: 'الخدمات',
    settingsKeys: 'بيانات الإعدادات',
    about: {
      identity: 'الألوان التي تظهر بها المنصة لمستخدميها.',
      media: 'الشعارات، وصور صفحة تسجيل الدخول.',
      keys: 'اسم المنصة، وسطر التذييل في صفحاتها.',
      contact: 'ما تعرضه صفحة التواصل، وهل تعرض أصلا.',
      video: 'كيف تشغل فيديوهات الدورات: التقدم، والترجمة، والإيقاف.',
      userManual: 'الأدلة التي يستطيع المستخدمون فتحها، بكل لغة.',
      documentation: 'استيراد الأدلة المعتمدة لهذه النسخة.',
      notifications: 'صياغة كل تنبيه، وكم مرة ترسل التنبيهات التلقائية.',
      email: 'خادم البريد الذي تخرج منه كل رسالة.',
      messaging: 'واتساب وسلاك والرسائل النصية: أيها مفعل، وكيف يتصل.',
      reports: 'ما تتضمنه التقارير.',
      login: 'كيف يدخل المستخدمون: الترخيص، والمصادقة، ورمز التحقق، والقفل، والشروط.',
      registration: 'الحسابات التي تنشأ يدويا لا بالدعوة.',
      profile: 'الحقول التي يحملها الملف الشخصي، وأسماؤها.',
      sources: 'من أين يأتي المستخدمون: الدليل النشط وLDAP.',
      sync: 'متى تسحب بيانات المستخدمين من مصادرها.',
      servers: 'منصة موزعة على أكثر من خادم.',
      services: 'الخدمات التي تتواصل معها هذه المنصة.',
    },
  },

  /* The settings hub and the sheet each section opens in. */
  hub: {
    search: 'ابحث عن إعداد',
    noResults: 'لا يوجد إعداد بهذا الاسم',
    unsaved: 'تغييرات لم تحفظ',
    backToList: 'العودة إلى القائمة',
    discardTitle: 'تجاهل التغييرات؟',
    discardMessage: 'لم تحفظ التغييرات في هذا القسم.',
    discard: 'تجاهل',
    keepEditing: 'متابعة التحرير',
    close: 'إغلاق',
    readOnly: 'للعرض فقط',
    storedOnly: 'تعرض كما هي مخزنة، ولا يمكن تعديلها من هنا حاليا.',
    copy: 'نسخ الرابط',
    inArabic: 'بالعربية',
    inEnglish: 'بالإنجليزية',
  },

  /* Headings inside the sheets. */
  sheet: {
    logos: 'الشعارات',
    signIn: 'صفحة الدخول',
    platform: 'المنصة',
    address: 'العنوان',
    connection: 'الاتصال بالخادم',
    sender: 'المرسل',
    guideFiles: 'ملفات الدليل',
    guideDisplay: 'طريقة العرض',
    adminGuide: 'دليل لوحة الإدارة',
    reportColumns: 'بيانات الملف الشخصي في التقارير',
    field: 'الحقل',
    policiesReport: 'السياسات',
    examsReport: 'الاختبارات والبرامج',
    footer: 'نص التذييل',
    orgName: 'اسم المنظمة',
  },

  tabs: {
    media: 'الملفات المرئية', // Media_Files
    keys: 'اسم المنصة', // Application_Title
    contact: 'صفحة التواصل', // Contact_Page
    video: 'إعدادات الفيديو', // Video_Settings
    userManual: 'دليل المستخدم', // User_Guide
    reports: 'إعدادات التقارير', // Reports_Settings
    notifications: 'إعدادات التنبيهات', // Notifications_Settings
    messaging: 'قنوات التنبيه',
    email: 'إعدادات البريد',
  },

  /* Email + messaging — built in part 2. Labels are the product's own resource
     entries (EmailSMTPSettings, From, SenderName, Host, Port, Username, Password,
     ApiKey, ApiUrl, Whatsapp, Slack, SMS, "Test Email Sending"). */
  email: {
    title: 'إعدادات ارسال البريد', // EmailSMTPSettings
    host: 'المستضيف', // Host
    port: 'رقم المنفذ', // Port
    portHint: 'رقم المنفذ المستخدم للاتصال بـ SMTP (عادة 587 أو 465)',
    from: 'بريد المرسل', // From
    fromHint: 'العنوان الذي يستخدم فعليا كـ SMTP Envelope (MAIL FROM)',
    senderName: 'اسم جهة الإرسال', // SenderName
    username: 'اسم المستخدم', // Username
    password: 'كلمة المرور', // Password
    ssl: 'خيارات التشفير',
    sslOptions: {
      None: 'بدون تشفير',
      Auto: 'تلقائي',
      SslOnConnect: 'SSL عند الاتصال',
      StartTls: 'STARTTLS',
      StartTlsWhenAvailable: 'STARTTLS عند توفره',
    },
    serviceState: 'حالة الخدمة',
    skipCertCheck: 'تجاهل التحقق من شهادة الخادم',
    test: 'تجربة إرسال بريد', // Test Email Sending
    testHint: 'يتم الإرسال بالبيانات الموجودة في النموذج الآن، قبل حفظها.',
    testTitle: 'تجربة إرسال بريد', // Test_Email_Sending
    testAddress: 'البريد المستخدم للاختبار', // Email_Address_for_Testing
    testAddressHint: 'تصل رسالة الاختبار إلى هذا العنوان',
    testSend: 'إرسال', // Send_Email
  },

  messaging: {
    whatsapp: 'واتساب', // Whatsapp
    slack: 'سلاك', // Slack
    sms: 'رسالة نصية', // SMS
    unavailable: 'هذه القنوات غير متاحة حاليا ولا ترسل رسائل. استخدم البريد الإلكتروني أو الإشعارات داخل النظام.',
    off: 'غير متاحة',
    enabled: 'مفعل', // Enabled
    apiUrl: 'رابط الخدمة', // ApiUrl
    apiKey: 'مفتاح الخدمة', // ApiKey
    senderNumber: 'رقم المرسل',
    senderId: 'معرف المرسل',
    webhookUrl: 'رابط Webhook',
    botToken: 'رمز البوت',
    channel: 'القناة',
  },

  servicesUrl: {
    title: 'رابط الخدمات',
    label: 'رابط الخدمات',
  },

  media: {
    whiteLogo: 'الشعار الأبيض', // White_Logo
    whiteLogoHint:
      'يستخدم هذا الشعار في واجهة مدير النظام، وواجهة المستخدم، وصفحة عرض الفيديو. يفضل أن يكون الشعار باللون الأبيض وبخلفية شفافة. إذا لم يكن الشعار الأبيض متوفرا، يمكن رفع الشعار العادي، ثم تغيير خلفية الشعار إلى "إظهار"، ليتم وضع إطار حوله وعرضه بشكل سليم على الموقع.', // This_logo_is_used_in_the_admin_interface
    whiteLogoSpec: 'PNG (590 X 345)',
    logoBackground: 'خلفية الشعار', // inline in the view, not a resx key
    mainLogo: 'الشعار الأساسي', // Main_Logo
    mainLogoHint:
      'يستخدم هذا الشعار في صفحة تسجيل الدخول، والتقارير المصدرة، وصفحة السياسات، بالإضافة إلى ظهوره عند التمرير لأسفل في لوحة المستخدم وعند تغيير لون القائمة الرئيسية.', // This_logo_is_used_on_the_login_page
    sideImage: 'الصورة الجانبية للدخول', // Login_Side_Image
    sideImageSpec: 'PNG (792 X 1200)',
    arabicImage: 'الصورة الجانبية العربية', // Arabic_Image
    englishImage: 'الصورة الجانبية الإنجليزية', // English_Image
    loginAndLogo: 'صفحة الدخول & الشعار', // Login_Page & Logo
    loginTheme: 'لون شاشة الدخول', // LoginTheme
    white: 'أبيض', // White
    black: 'أسود', // Black
    logoOnLogin: 'الشعار في صفحة الدخول', // inline in the view
    background: 'صورة خلفية الدخول', // Login_Background_Image
    backgroundSpec: 'JPG (2048 X 1365)',
    imageAr: 'الصورة بالعربية', // General.lblImageAr
    imageEn: 'الصورة بالإنجليزية', // General.lblImageEn
    favicon: 'ايقونة المتصفح', // Browser_Icon
    faviconSpec: 'PNG (48 X 48)',
  },

  keys: {
    appTitle: 'اسم المنصة', // Application_Title
    arabicAppTitle: 'اسم البرنامج العربي', // Arabic_Application_Title
    englishAppTitle: 'اسم البرنامج الإنجليزي', // English_Application_Title
    footer: 'نص نهاية الموقع', // Website_Footer_Text
    footerAr: '(بالعربية) نص نهاية الموقع', // Website_Footer_Text__Arabic_
    footerEn: '(بالإنجليزية) نص نهاية الموقع', // Website_Footer_Text__English_
    organization: 'بيانات المنظمة', // Organization_Data
    arabicOrgName: 'اسم المنظمة العربي', // Arabic_Organization_Name
    englishOrgName: 'اسم المنظمة الإنجليزي', // English_Organization_Name
    orgEmail: 'البريد الإلكتروني للمنظمة', // Organization_Email
    orgEmailHint:
      'يستخدم هذا البريد الإلكتروني لتنبيه مدير النظام بتسجيل حوادث سيبرانية جديدة. يجب أن يكون مرتبط بمستخدم مسجل في النظام و يمتلك صلاحيات مدير النظام. يمكنك إدخال عدة عناوين بريد إلكتروني مفصولة بفاصلة (،) هنا. تأكد من أن كل بريد إلكتروني مرتبط بمستخدم مسجل', // OrgEmailHint
    domain: 'رابط المنصة', // Domain_URL
    domainNote: '(يستخدم في التنبيهات)', // _Used_in_notifications_
    domainSpec: 'https://www.Website-Name.com',
    useHsts: 'فرض الاتصال الآمن عبر HTTPS (HSTS)', // UseHsts
  },

  contact: {
    title: 'صفحة التواصل', // Contact_Page
    allDisabledWarning: 'تنبيه، كافة العناصر غير مفعلة. فضلا فعل عنصر واحد على الأقل!', // MessageWarningContactUsInactiveItems
    enablePage: 'إظهار شاشة معلومات التواصل', // Show_Support_Contact_Info_Page
    pageTitle: 'عنوان الصفحة', // Page_Title
    pageHeader: 'نص رأس الصفحة', // Page_Header_Text
    enableEmail: 'تفعيل البريد الإلكتروني', // ChkBxEnableEmail
    emailAddress: 'البريد الالكتروني', // Email_Address
    emailOptions: 'طريقة  عرض البريد في الصفحة', // LabelContactUsEmailOptions
    showSendForm: 'إظهار صندوق إرسال رسالة', // chkBxShowSendEmailForm
    showEmailInfo: 'إظهار معلومات البريد الإلكتروني', // chkBxShowEmailInfo
    emailLabel: 'نص عنوان البريد الإلكتروني', // Email_Label_Text
    showPhone: 'إظهار رقم الهاتف', // ChkBxShowPhone
    phone: 'رقم الهاتف', // Contact_Phone
    phoneLabel: 'نص عنوان هاتف التواصل', // Phone_Label_Text
  },

  video: {
    title: 'إعدادات الفيديو', // Video_Settings
    progressBar: 'تفعيل شريط التقدم', // Enable_Progress_bar
    progressBarHint:
      'فعل هذا الخيار لتمكين المستخدمين من التحرك للأمام أو الخلف داخل الفيديو (التنقل). يفيد هذا الخيار في مقاطع الفيديو الطويلة حيث قد يرغب المشاهد في تخطي أجزاء معينة أو العودة إليها', // Allow_Seeking_in_Videos
    forceSubtitle: 'تفعيل إلزامية الترجمة', // Enable_Force_Subtitle
    forceSubtitleHint:
      'فعل هذا الخيار لعرض الترجمة دائما عند تشغيل الفيديو، بغض النظر عن إعدادات المشاهد. يفيد هذا الخيار لتحسين الوصول أو عند وجود محتوى بلغة غير مفهومة للمستخدم', // Force_Subtitles_in_Videos
    userSubtitleControl: 'السماح للمستخدمين بتشغيل أو إيقاف الترجمة أثناء التشغيل', // enableUserSubtitleControl
    pauseOnBlur: 'إيقاف مشغل الفيديو في حال انتقل المستخدم لصفحة أخرى', // Stop_video_playing_when_window_loses_focus
    pauseOnBlurHint:
      'فعل هذا الخيار لإيقاف تشغيل الفيديو تلقائيا عندما ينتقل المستخدم إلى تبويب آخر أو يترك الصفحة الحالية. يضمن هذا أن لا يفوت المشاهد أي جزء من المحتوى', // Pause_Video_on_Tab_Change
    playbackRates: 'تفعيل التحكم في سرعة تشغيل الفيديو', // Enable_playback_rates
    playbackRatesHint:
      'فعل هذا الخيار لتمكين المستخدمين من تعديل سرعة تشغيل الفيديو (مثل التشغيل البطيء أو السريع). يفيد هذا الخيار في التعلم أو المراجعة أو تصفح المحتوى بسرعة مناسبة', // Allow_Playback_Speed_Control
  },

  userManual: {
    guide: 'دليل المستخدم', // User_Guide
    guideAr: 'دليل المستخدم بالعربية', // General.User_Guide_AR
    guideEn: 'دليل المستخدم بالإنجليزية', // General.User_Guide_EN
    settings: 'إعدادات دليل المستخدم', // User_Guide_Settings
    popup: 'إظهار دليل المستخدم  عند الدخول في نافذة منبثقة', // Show_user_manual_upon_entry_in_a_popup_window
    introVideo: 'إظهار فيديو بدلا من دليل المستخدم', // Show_a_video_instead_of_a_user_manual
    linkOnLogin: 'إظهار رابط دليل المستخدم في صفحة الدخول', // Show_the_user_guide_link_on_the_login_page
    separatePage: 'إظهار دليل المستخدم في صفحة منفصلة', // Show_the_user_guide_on_a_separate_page
    adminUrl: 'رابط دليل المستخدم للوحة مدير النظام', // AdminPanelUserManualUrl
    published: 'منشور',
    notPublished: 'لم يُنشر دليل بهذه اللغة بعد',
    view: 'عرض',
    viewInPortal: 'معاينته كما يراه المستخدمون',
  },

  reports: {
    title: 'إعدادات التقارير', // Reports_Settings
    userStatistics: 'تفعيل صفحة الإحصائيات في واجهة المستخدم', // Enable_statistics_page_for_user
    policiesColumns: 'بيانات الملف الشخصي في تقرير السياسات', // Profile_Data_on_Policies_Report
    examsColumns: 'بيانات الملف الشخصي في تقرير الاختبارات والبرامج التدريبية', // Profile_Data_on_Exams_Report___Programs_Report
  },

  notifications: {
    title: 'إعدادات التنبيهات', // Notifications_Settings
    email: 'البريد الإلكتروني', // Email
    index: '#', // NotificationSettings.TableHead_1
    name: 'الاسم', // NotificationSettings.TableHead_2
    lastUpdated: 'آخر تحديث', // NotificationSettings.TableHead_3
    edit: 'تعديل', // Edit
    editTitle: 'تعديل قالب التنبيه',
    subject: 'عنوان البريد الإلكتروني',
    body: 'محتوى البريد الإلكتروني بصيغة HTML',
    bodyHint: 'يمكن استخدام المتغيرات الموجودة مسبقا في القالب. تطبق ألوان وشعار الجهة عند إرسال الرسالة.',
    preview: 'معاينة',
    noPlaceholders: 'لا يستخدم هذا القالب أي متغيرات.',
    showSamples: 'عرض بقيم تجريبية',
    insert: 'إدراج في الرسالة',
    discardTitle: 'تجاهل التغييرات؟',
    missing: 'قالب التنبيه غير موجود، ربما تم حذفه',
    discardMessage: 'لم تحفظ التغييرات على هذا القالب.',
    discard: 'تجاهل',
    keepEditing: 'متابعة التحرير',
    lastUpdatedAt: 'آخر تحديث',
    previewSubject: 'الموضوع',
    previewFrom: 'من',
    previewTo: 'إلى',
    previewHint: 'تعرض المتغيرات بقيم تجريبية. تطبق ألوان وشعار الجهة عند إرسال الرسالة.',
    placeholders: 'المتغيرات في هذا القالب',
    unknownPlaceholder: 'لا توجد قيمة تجريبية',
    autoDays: 'عدد الأيام للإشعارات التلقائية', // inline in the view
  },

  /**
   * NotificationEventsType values that get the "automatic notification days" button —
   * Course_Start, Course_End, New_Policy, Awareness, Assessment_New, Assessment_Required.
   * LanguageConstants.cs:79-103.
   */
  autoDaysTypes: [1, 2, 3, 5, 12, 13],

  /**
   * SettingsController.GetAllTemplates drops Users, Groups and Phishing — those events
   * have no editable template — so this tab lists 19 of the 22 rows. The سجل التنبيهات
   * filter uses the unfiltered list.
   */
  templatelessTypes: [6, 7, 14],

  /** Utility.ProfileAttributes — the report column checkboxes, ordered by Arabic name. */
  profileAttributes: [
    { name: 'Username', label: 'اسم المستخدم' },
    { name: 'Fullname', label: 'الاسم الكامل' },
    { name: 'Email', label: 'البريد الإلكتروني' },
    { name: 'Department', label: 'الإدارة' },
    { name: 'Gender', label: 'الجنس' },
    { name: 'EmployeeId', label: 'رقم الموظف' },
    // Attribute1..5 take their label from the Attribute<n>_AR_Name settings.
    { name: 'Attribute1', setting: 'Attribute1_AR_Name' },
    { name: 'Attribute2', setting: 'Attribute2_AR_Name' },
    { name: 'Attribute3', setting: 'Attribute3_AR_Name' },
    { name: 'Attribute4', setting: 'Attribute4_AR_Name' },
    { name: 'Attribute5', setting: 'Attribute5_AR_Name' },
  ],
}

export const advancedStrings = {
  title: 'إعدادات متقدمة', // Advanced_Settings

  /* Headings and labels inside the advanced sheets. */
  sheet: {
    signInMethod: 'طريقة الدخول',
    lockout: 'قفل الحساب',
    terms: 'شروط الاستخدام',
    visible: 'يظهر',
    editable: 'يعدله المستخدم',
    field: 'الحقل',
    afterSignIn: 'بعد تسجيل الدخول',
    openProfile: 'فتح صفحة الملف الشخصي بعد تسجيل الدخول',
    customNames: 'أسماء الحقول الخاصة',
    arabic: 'العربية',
    english: 'English',
    syncSources: 'مصادر السحب',
    dataset: 'البيانات',
    source: 'المصدر',
    method: 'الطريقة',
    depts: 'الإدارات',
    allUsers: 'كل المستخدمين',
    userInfo: 'بيانات مستخدم واحد',
    readProfile: 'قراءة الملف الشخصي',
    userFetch: 'المستخدم عند الدخول',
    autoSync: 'السحب التلقائي',
    notificationService: 'خدمة الإشعارات',
    serviceStatus: 'حالة الخدمات',
    service: 'الخدمة',
    interval: 'الفاصل',
    running: 'تعمل',
    stopped: 'متوقفة',
    sourcesEmpty: 'لا توجد مصادر بيانات',
    serversEmpty: 'لا توجد خوادم إضافية',
    registrationLink: 'رابط التسجيل',
    accounts: 'الدعوات واستعادة كلمة المرور',
  },

  tabs: {
    login: 'إعدادات الدخول', // Login_Settings
    profile: 'إعدادات الملف الشخصي', // User_Profile_Settings
    sources: 'إعدادات مصادر الربط', // Data_Sources_Settings
    sync: 'إعدادات سحب البيانات', // Data_Sync_Settings
    registration: 'إعدادات التسجيل اليدوي', // Manual_Registration_Settings
    servers: 'إعدادات الخوادم المتعددة', // MultiServerSettings
    services: 'إعدادات الخدمات', // ServicesSettings
  },

  login: {
    general: 'إعدادات عامة', // General_Settings
    generalSubtitle: 'تطبق على الحقول في صفحة الملف الشخصي', // Applies_to_fields_on_the_profile_page
    licenseType: 'رخصة النظام', // CompanyProfile.Label7Resource1_Text
    authMethod: 'طريقة الدخول للنظام', // CompanyProfile.Label6Resource1_Text
    authMethodOptions: {
      [AUTHENTICATION_METHOD.LOCAL]: 'حسابات محلية',
      [AUTHENTICATION_METHOD.ACTIVE_DIRECTORY]: 'Active Directory',
      [AUTHENTICATION_METHOD.ACTIVE_DIRECTORY_AND_LOCAL]: 'Active Directory + حسابات محلية',
      [AUTHENTICATION_METHOD.PASS_LINK]: 'رابط دخول بدون كلمة مرور',
      [AUTHENTICATION_METHOD.ENTRA_ID]: 'Microsoft Entra ID',
      [AUTHENTICATION_METHOD.ENTRA_ID_AND_LOCAL]: 'Microsoft Entra ID + حسابات محلية',
    },
    authSource: 'مصدر بيانات المصادقة', // Authentication_Source
    loginPage: 'صفحة الدخول', // Login_Page
    ldapSso: 'الدخول الموحد LDAP SSO', // LDAP_SSO
    usernameFormat: 'عند استخدام عنوان البريد الإلكتروني كاسم مستخدم أثناء عملية تسجيل الدخول', // UsernameFormatOptions
    usernameFormatOptions: [
      { value: USERNAME_FORMAT_CODE.UNCHANGED, label: 'إتمام عملية تسجيل الدخول بدون عمل شئ' }, // _DoNothing
      {
        value: USERNAME_FORMAT_CODE.EXTRACT_USERNAME,
        label:
          'استخرج اسم المستخدم من عنوان البريد الإلكتروني (على سبيل المثال، user123@example.com سيعطي user123) واستخدمه لتسجيل الدخول',
      }, // _ExtractUsernameFromEmail
      {
        value: USERNAME_FORMAT_CODE.REJECT_EMAIL,
        label:
          'أوقف عملية تسجيل الدخول وأبلغ المستخدم باستخدام اسم المستخدم فقط بدلا من عنوان بريده الإلكتروني الكامل.',
      }, // _ShowWarningMessage
    ],

    otp: 'كلمة المرور لمرة واحدة', // One_Time_Password
    otpEnabled: 'تمكين كلمة المرور لمرة واحدة', // One_Time_Password_Enabled
    otpTarget: 'الفئة المستهدفة', // TargetedGroups
    otpTargets: [
      { value: 'Admins', label: 'مدراء النظام فقط' }, // AdminsOnly
      { value: 'Users', label: 'المستخدمين فقط' }, // UsersOnly
      { value: 'All', label: 'تحديد الكل' }, // Select_All
    ],
    otpLength: 'طول كلمة المرور لمرة واحدة', // One_Time_Password_Length
    otpValidPeriod: 'فترة صلاحية كلمة المرور لمرة واحدة (ثواني)', // One_Time_Password_Valid_Period__Seconds_

    lockout: 'إيقاف مؤقت', // Account_Lockout_Settings
    lockoutEnabled: 'تفعيل قفل الحساب في حالة إدخال كلمة مرور خاطئة', // Enable_Account_Lockout_When_Wrong_Password_Entry
    attempts: 'عدد محاولات إدخال كلمة المرور', // Attempts_Before_Lockout
    lockoutWindow: 'مدة إغلاق الحساب بالدقائق', // Account_Lockout_Period_in_Minutes
    sessionTimeout: 'عدد دقائق الجلسة بعد تسجيل الدخول', // Session_Timeout_In_Minutes

    terms: 'إعدادات شروط الاستخدام', // TermsOfUseSettings.PageResource1_Title
    termsAr: 'شروط الاستخدام العربية', // TermsOfUseSettings.ArabicLabel
    termsEn: 'شروط الاستخدام الإنجليزية', // TermsOfUseSettings.EnglishLabel
    termsEnable: 'تفعيل صفحة شروط الاستخدام', // TermsOfUseSettings.EnableTermsOfUseCkBx_Text
    termsEnableNote:
      '(تفعيل هذا الخيار سوف يؤدي إلى عرض شروط الاستخدام على المستخدم قبل استخدام النظام)', // TermsOfUseSettings.EnableLabel
    termsUpdated:
      'تحديث شروط الاستخدام و إلزام المستخدمين على الموافقة قبل استكمال استخدام النظام', // TermsOfUseSettings.ChkBxUpdated_Text
    termsUpdatedNote: '(قم بوضع علامة على الاختيار التالي في حالة أن هذا تحديث لشروط الاستخدام)', // TermsOfUseSettings.UpdateNotice
    termsSubmit: 'حفظ', // TermsOfUseSettings.BtnSubmit_Text
  },

  profile: {
    title: 'ربط الملف الشخصي', // ProfileMapping.PageResource1_Title
    chooseCompany: 'اختر المنظمة', // ProfileManagement.ChooseCompanyList_Text
    fieldsHeading: 'فضلا قم باختيار الحقول التي يجب أن تظهر في صفحة الملف الشخصي', // ProfileManagement.lblTitleResource1_Text
    allowEdit: 'السماح بالتعديل بواسطة المستخدم', // ProfileManagement.ListItemResource11_Text

    /** ProfileMappingController.Index — the field list and its order. */
    fields: [
      { name: 'EnglishName', label: 'الاسم باللغة الإنجليزية' }, // ListItemResource1_Text
      { name: 'ArabicName', label: 'الاسم باللغة العربية' }, // ListItemResource2_Text
      { name: 'Gender', label: 'الجنس' }, // ListItemResource3_Text
      { name: 'Email', label: 'البريد الإلكتروني' }, // ListItemResource4_Text
      { name: 'Phone', label: 'الهاتف' }, // ListItemResource5_Text
      { name: 'Mobile', label: 'الهاتف المحمول' }, // ListItemResource6_Text
      { name: 'DepartmentId', label: 'الإدارة' }, // ListItemResource7_Text
      { name: 'EmployeeId', label: 'رقم الموظف' }, // ListItemResource8_Text
      { name: 'Username', label: 'اسم المستخدم' }, // ListItemResourceUserName_Text
      // Attribute1..5 take their label from the Attribute<n>_AR_Name settings.
      { name: 'Attribute1', setting: 'Attribute1_AR_Name' },
      { name: 'Attribute2', setting: 'Attribute2_AR_Name' },
      { name: 'Attribute3', setting: 'Attribute3_AR_Name' },
      { name: 'Attribute4', setting: 'Attribute4_AR_Name' },
      { name: 'Attribute5', setting: 'Attribute5_AR_Name' },
    ],
    /** The view disables the ninth "allow edit" toggle — Username is never user-editable. */
    lockedEditIndex: 8,

    customAttributes: 'أسماء بيانات الملف الشخصي الخاصة', // Custom_Profile_Attributes_Names
    attribute: 'الخاصية', // Attribute
    arabicName: 'الإسم بالعربية:', // Arabic_Name_
    englishName: 'الإسم بالإنجليزية:', // English_Name_

    otherProperties: 'خصائص أخرى', // Other_Properties
    allowEditPageTitle: 'هل تريد الانتقال لصفحة تعديل الملف الشخصي بعد تسجيل الدخول؟', // ProfileManagement.AllowProfileEditPageTitle_Text
    allowEditPage: 'نعم، انتقل لصفحة تحديث الملف الشخصي', // ProfileManagement.AllowProfileEditPageResource_Text
    forceGroupLabel: 'إجبار المستخدم على تغيير مجموعة حالية مستخدمة', // ProfileMapping.ForceChangeGroupLabel
    forceGroupCheck: 'تفعيل إجبار المستخدم على تغيير هذه المجموعة في حالة إن كان ينتمي إليها', // ProfileMapping.ForceChangeGroupChkBx
    forceGroupSelect: 'المجموعات', // ProfileMapping.ForceChangeGroupSelect
  },

  sources: {
    name: 'اسم المصدر',
    actions: 'الإجراءات',
    remove: 'حذف',
    removeConfirm: 'هل تريد حذف مصدر البيانات هذا؟ لن يتمكن المستخدمون من تسجيل الدخول أو المزامنة عبره.',
    removed: 'تم حذف مصدر البيانات',
    legacyType: 'نوع قديم غير مدعوم',
    domain: 'نطاق الدليل (اختياري)',
    loginDomainHint: 'لاسم دخول الموظف دون نطاق، اترك الحقل فارغا لإرساله كما هو. أدخل النطاق لإرسال username@domain. يحتفظ الاسم DOMAIN\\username بنطاقه المحدد. لا يضاف مسار DC إلى اسم دخول الموظف.',
    bindUsernameHint: 'يستخدم للاستعراض والمزامنة واختبار الاتصال بشكل مستقل عن كلمة مرور الموظف. أدخل DOMAIN\\username أو username@domain لتحديد نطاق الحساب. للاسم دون نطاق يستخدم النطاق المحدد، وإذا كان فارغا يؤخذ نطاق حساب الاتصال من مسار DC.',
    tenantId: 'معرف مستأجر Microsoft Entra أو النطاق الموثق',
    clientId: 'معرف التطبيق (العميل)',
    clientSecret: 'سر العميل',
    password: 'كلمة مرور الربط',
    secretUnchanged: 'اتركه فارغا للاحتفاظ ببيانات الاعتماد المحفوظة.',
    graphEndpoint: 'عنوان Microsoft Graph',
    odataFilter: 'مرشح مستخدمي Microsoft Graph الاختياري',
    ldapFilter: 'مرشح بحث مستخدمي LDAP',
    accessGroup: 'معرف أو اسم مجموعة الوصول المسموح بها (اختياري)',
    adminGroups: 'معرفات أو أسماء مجموعات مسؤولي روام (اختياري)',
    groupsNote: 'استخدم أسماء المجموعات المطابقة أو الاسم المميز في LDAP أو معرف مجموعة Entra. افصل المجموعات بفاصلة منقوطة.',
    resignedAction: 'التعامل مع المستخدمين المستقيلين',
    resignedActionNone: 'لا يتم التعامل معهم',
    resignedActionHide: 'إخفاؤهم تلقائياً',
    resignedActionAdmin: 'بواسطة مدير النظام',
    resignMissing: 'مستقيل إذا لم يعد الدليل يرجعه',
    resignMissingHint: 'بعد مزامنة المستخدمين، يُعد مستقيلاً كل حساب من هذا المصدر لم يعد الدليل يرجعه ولا يُعثر عليه باسم المستخدم.',
    resignInData: 'مستقيل إذا وسمته بيانات الدليل',
    resignInDataHint: 'يُقرأ في كل مزامنة للمستخدمين.',
    resignedVariable: 'متغير الموظف المستقيل',
    resignedVariableHint: 'التنسيق المطلوب: Key=Value. إن تُرك فارغاً، يُعد الحساب المعطّل في الدليل مستقيلاً.',
    detectUsernameChange: 'الاحتفاظ بالحساب عند تغيير اسم المستخدم في LDAP',
    detectUsernameChangeHint: 'عند الدخول، يطابق البريد الفريد ضمن هذا المصدر مع الاحتفاظ بالحساب وسجل التعلم.',
    entraPrerequisites: 'سجل تطبيق Web في Microsoft Entra ID، وأضف عنوان موقع Rawam المنتهي بـ /signin-entra كعنوان إعادة التوجيه. امنح إذن التطبيق User.Read.All في Microsoft Graph مع موافقة المسؤول، وأضف GroupMember.Read.All عند استخدام مجموعات الوصول أو المسؤولين.',
    ssl: 'استخدام LDAP الآمن (LDAPS)',
    test: 'اختبار الاتصال',
    title: 'إعدادات مصادر الربط', // Data_Sources_Settings
    newSource: 'مصدر جديد', // New_Source
    type: 'النوع', // Type
    arabicName: 'الإسم بالعربية:', // Arabic_Name_
    englishName: 'الإسم بالإنجليزية:', // English_Name_
    username: 'اسم المستخدم', // Username
    connectionString: 'رابط المصدر', // Connection_String
    isActive: 'نشط', // ACTIVEE
    edit: 'تعديل', // Edit
    browseUsers: 'استعراض المستخدمين',
    browseGroups: 'استعراض الإدارات',
    profileMapping: 'ربط السمات والخصائص',
    queryProperties: 'استكشاف خصائص الدليل النشط',
    usernameToExplore: 'اسم المستخدم لاستكشاف الخصائص (اختياري)',
    exploreButton: 'استكشاف الخصائص',
    builtInField: 'حقل روام',
    sourceAttribute: 'خاصية الدليل النشط',
    notMapped: '-- غير مربوط --',
    saveMapping: 'حفظ الربط',
    mappingSaved: 'تم حفظ ربط السمات بنجاح',
    statusResigned: 'مستقيل',
    statusActive: 'نشط',
    noUsersFound: 'لم يتم العثور على مستخدمين في مصدر البيانات',
    noGroupsFound: 'لم يتم العثور على إدارات في مصدر البيانات',
    discoveredAttributes: 'الخصائص المكتشفة',
    samplePropertiesPreview: 'معاينة خصائص العينة',
    value: 'القيمة',
    resignedField: 'حالة الاستقالة',
  },

  sync: {
    legacyLdap: 'جميع مصادر LDAP الفعالة (إعداد الإصدار السادس)',
    title: 'إعدادات سحب البيانات', // Data_Sync_Settings
    adIntegration: 'سياسات السحب',
    deptsSource: 'مصدر سحب الإدارات', // Departments_Integration_Data_Source
    usersSource: 'مصدر سحب بيانات كل المستخدمين', // All_Users_Integration_Data_Source
    userFetchSource: 'مصدر سحب بيانات المستخدم', // User_Fetch_Integration_Data_Source
    sourceOptions: [
      { value: 'WebAPI', label: 'ويب' }, // WebAPI
      { value: 'ActiveDirectory', label: 'مجلد نشط' }, // ActiveDirectory
      { value: 'None', label: 'بدون' }, // None
    ],
    deptsType: 'طريقة ربط بيانات الإدارات', // Departments_Integration_Type
    usersType: 'طريقة ربط كل المستخدمين', // Users_Integration_Type
    userFetchType: 'طريقة ربط سحب بيانات المستخدم', // User_Fetch_Integration_Type
    import: 'استيراد', // Import
    syncOnly: 'مزامنة', // Sync
    importSync: 'مزامنة و استيراد', // ImportSync

    autoTitle: 'إعدادات التزامن التلقائي', // Auto_Sync_Settings
    deptsAutoFetch: 'السحب التلقائي للإدارات', // Departments_Auto_Fetch
    usersAutoFetch: 'السحب التلقائي للمستخدمين', // Users_Auto_Fetch
    delay: 'مدة الانتظار قبل التشغيل التالي', // Delay_Before_Next_Run
    overLicense: 'عند سحب مستخدمين وكان العدد أكبر من الرخصة', // When_Fetched_Users_Count_Greater_Than_License
    overLicenseOptions: [
      { value: 'Reject', label: 'رفض عملية السحب' }, // Reject_Fetch_Operation
      { value: 'AsLicenseCount', label: 'سحب المستخدمين بعدد الرخص المتاحة"' }, // Take_As_License_Count
    ],
    deleteEmptyGroups: 'حذف مجموعات الدليل النشط التي لا تحتوي على مستخدمين', // Delete_Active_Directory_Groups_With_No_Users
    commandMode: 'كيفية كتابة الملفات الشخصية المسحوبة في قاعدة البيانات', // SyncProfilesCommandMode
    commandModeOptions: [
      { value: 'bulkSql', label: 'SQL' },
      { value: 'bulkEF', label: 'ORM' },
    ],
  },

  registration: {
    title: 'إعدادات التسجيل اليدوي', // Manual_Registration_Settings
    activateInvitations: 'تفعيل نظام الدعوات', // ActivateInvitations
    showRegistrationLink: 'تفعيل خاصية التسجيل اليدوي في النظام', // Activate_the_manual_registration_feature_in_the_system
    activateResetPassword: 'تفعيل خاصية نسيت كلمة السر في النظام', // Activate_forget_password_feature_in_the_system
    activationMethod: 'طريقة تفعيل الحساب', // Account_activation_method
    activationOptions: [
      { value: String(USER_ACTIVATION_METHOD.EMAIL), label: 'تلقائي' }, // Automatic
      { value: String(USER_ACTIVATION_METHOD.ADMIN), label: 'يدوي بواسطة مدير النظام' }, // Manual_By_Admin
    ],
    emailOnDomain: 'البريد يجب أن يكون بداخل دومين المنظمة', // CompanyProfile.InsideDomainResource1_Text
    domains: 'دومين المنظمة', // CompanyProfile.Label5Resource1_Text
    copyLink: 'انسخ رابط التسجيل', // CompanyProfile.Label2Resource1_Text
    copied: 'تم نسخ رابط التسجيل',
    copyFailed: 'تعذر النسخ، انسخ الرابط يدويا',
    registerLink: 'التسجيل', // CompanyProfile.regLinkResource1_Text
    noteTitle: 'ملاحظة عند التسجيل اليدوي', // Note_on_manual_registration
    noteBody:
      'بعد تسجيل المستخدم سيتم ارسال بريد الى المستخدم للتحقق من صحة البريد وبعدها سيتم تفعيل الحساب بناء على طريقة التفعيل المحددة', // After_the_user_registers…
  },

  servers: {
    title: 'إعدادات الخوادم المتعددة', // MultiServerSettings
    newServer: 'خادم جديد', // NewServer
    serverId: 'رقم الخادم', // TableHeaderServerId
    serverUrl: 'عنوان الخادم', // TableHeaderServerUrl
    edit: 'تعديل', // Edit
    actions: 'الإجراءات',
    remove: 'حذف',
    removeConfirm: 'هل تريد حذف هذا الخادم؟',
    removed: 'تم حذف الخادم',
  },

  services: {
    title: 'إعدادات الخدمات', // ServicesSettings
    notifications: 'الإشعارات', // Notifications
    phishing: 'صياد', // Phishing
    sync: 'مزامنة', // Sync
    emailSettings: 'إعدادات خدمة البريد', // Email_Service_Settings
    enableNotificationService: 'تفعيل خدمة الإشعارات', // inline in the view
    enableNotificationServiceHint: 'تفعيل الخدمة',
    maxRetries: 'أقصى عدد لمحاولات إعادة الإرسال', // MaxRetries
    maxRetriesHint: 'ستتم إعادة محاولة إرسال الرسائل حتى تصل إلى الحد الأقصى للمحاولات.',
    maxTaskMinutes: 'أقصى مدة لإرسال الإشعار', // MaxTaskExecutionMinutes
    maxTaskMinutesHint:
      'الحد الأقصى للوقت (بالدقائق) الذي يمكن أن تستغرقه المهمة لمعالجة البريد الإلكتروني. هذا يمنع تنفيذ مهمتين على نفس الرسالة في نفس الوقت.',
    statusHintNotifications: 'تفعيل الخدمة - هذا الخيار مطلوب مع خدمة الإشعارات',
    statusHint: 'تفعيل الخدمة',
    delayHint: 'مدة الانتظار قبل إعادة تشغيل الخدمة مرة أخرى',
    logs: 'سجلات الكونسول', // ConsoleLogs
    logsHint: 'تفعيل طباعة سجلات الخدمة',
    maxProcessed: 'أقصى عدد من السجلات للمعالجة', // MaxProcessedEntries
    maxProcessedHint: 'الحد الأقصى لعدد المهام التي يمكن للخدمة اختيارها معالجتها في دورة تشغيل واحدة.',
    maxParallelism: 'الحد الأقصى للمهام المتوازية', // MaxDegreeOfParallelism
    maxParallelismHint: 'الحد الأقصى لعدد المهام التي يمكن أن تنفذها الخدمة في وقت واحد.',

    /** The three accordion groups and their services — ProfileMapping/Index.cshtml:10-59. */
    groups: [
      {
        id: 'notifications',
        items: [
          { id: 'NotificationsCreation', name: 'إنشاء الإشعارات', maxProcessed: true }, // NotificationsCreation
          {
            id: 'NotificationsSending',
            name: 'إرسال الإشعارات', // NotificationsSending
            maxProcessed: true,
            maxParallelism: true,
          },
          { id: 'MarkFailedNotifications', name: 'تحديد الإشعارات الفاشلة' }, // MarkFailedNotifications
        ],
      },
      {
        id: 'phishing',
        items: [
          { id: 'Phishing', name: 'صياد' }, // Phishing
          { id: 'EmailListener', name: 'قراءة رسائل البريد الإلكتروني' }, // EmailListener
        ],
      },
      {
        id: 'sync',
        items: [
          { id: 'SyncDepts', name: 'السحب التلقائي للإدارات' }, // Departments_Auto_Fetch
          { id: 'SyncUsers', name: 'السحب التلقائي للمستخدمين' }, // Users_Auto_Fetch
        ],
      },
    ],
  },
}

export const settingsKeysStrings = {
  /* The consistency check the Razor page carried and the React one lacked: each enum the
     code compiles against, against the database rows meant to back it. */
  reference: {
    title: 'بيانات مرجعية',
    status: 'الحالة', // Status
    present: 'موجود', // NotFound's opposite
    missing: 'غير موجود', // NotFound
    group: 'المجموعة',
    id: 'المعرف',
    enumName: 'الاسم البرمجي',
    arabicName: 'الاسم العربي', // NameAr
    englishName: 'الاسم الإنجليزي', // NameEn
    name: 'الاسم', // Name
    editTitle: 'تعديل الأسماء',
    add: 'إضافة',
    readOnly: 'قوالب التنبيهات تعدل من شاشة التنبيهات، وتعرض هنا للتحقق فقط.',
    ffmpeg: 'أداة معالجة الفيديو (ffmpeg)',
    groups: {
      activities: 'أنواع الأنشطة',
      groupTypes: 'أنواع الإدارات',
      difficultyLevels: 'مستويات الصعوبة',
      courseLevels: 'مستويات الدورات',
      authMethods: 'طرق تسجيل الدخول',
      templates: 'قوالب التنبيهات',
    },
  },
  title: 'بيانات الإعدادات', // Sitemap.SettingsValues
  databaseValue: 'القيمة في قاعدة البيانات', // DatabaseValue
  defaultValue: 'القيمة الإفتراضية', // DefaultValue
  none: 'بدون', // None
  key: 'المفتاح', // Key
  edit: 'تعديل القيمة',
  useDefault: 'استخدام القيمة الافتراضية',
  locked: 'بيانات اعتماد، تعدل من شاشة الإعدادات الخاصة بها',
}

export const documentationStrings = {
  title: 'أدلة الاستخدام',
  hint: 'استورد حزمة الأدلة المعتمدة من مركز روام والمطابقة لهذه النسخة. يظهر دليل المستخدم بعد ذلك في صفحة دليل المستخدم.',
  installed: 'الأدلة المثبتة',
  installedHint: 'ما لدى هذه النسخة، بلغة اللوحة.',
  available: 'مثبت',
  notImported: 'لم يستورد بعد',
  open: 'فتح',
  pdf: 'PDF',
  importTitle: 'استيراد حزمة',
  file: 'حزمة الأدلة الموقعة',
  upload: 'استيراد الحزمة',
  busy: 'جار الاستيراد…',
  success: 'تم استيراد الدليل بنجاح.',
  failure: 'تعذر الاستيراد. تحقق من التوقيع وهوية العميل والإصدار.',
  tooLarge: 'حجم الحزمة يتجاوز الحد المسموح (150 ميجابايت).',
  notConfigured: 'استيراد الأدلة غير مهيأ في هذه النسخة: لا يوجد مفتاح توقيع موثوق أو رقم إصدار.',
  admin: 'دليل المدير',
  adminHint: 'لمن يديرون المنصة.',
  user: 'دليل المستخدم',
  userHint: 'للمتعلمين، ويظهر في صفحة دليل المستخدم.',
}
